import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Modal from "react-modal";
import { ContextProvider } from "./functions/ContextAPI";
import { routes } from "./routes/routesConfig";
import AccountLayout from "./components/Layout/AccountLayout";
import ErrorBoundary from "./components/ErrorBoundary";

function App() {
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Initialize and monitor user data from localStorage
  React.useEffect(() => {
    const loadUserFromStorage = () => {
      try {
        const userData = localStorage.getItem("repUser");
        if (userData) {
          const user = JSON.parse(userData);
          console.log("Representative user loaded:", user);
        }
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    };

    // Load user data on mount
    loadUserFromStorage();

    // Add a minimum loading time to prevent flashing
    setTimeout(() => {
      setIsInitializing(false);
    }, 800);

    // Listen for storage changes (including from login)
    const handleStorageChange = (e) => {
      if (e.key === 'repUser') {
        loadUserFromStorage();
      }
    };

    // Listen for custom events (for same-tab changes)
    const handleUserUpdate = () => {
      loadUserFromStorage();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('repUserDataUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('repUserDataUpdated', handleUserUpdate);
    };
  }, []);

  Modal.setAppElement("#root");
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  });

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-25 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-6 shadow-lg"></div>
          <p className="text-gray-700 font-semibold text-lg">Initializing Representative Portal...</p>
          <p className="text-gray-500 font-medium text-sm mt-2">Please wait while we set up your session</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ContextProvider>
          <Router>
            <Routes>
              {routes.map((route, index) =>
                route.isProtected ? (
                  <Route key={index} element={<AccountLayout />}>
                    <Route path={route.path} element={route.element} />
                  </Route>
                ) : (
                  <Route key={index} path={route.path} element={route.element} />
                )
              )}
            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </Router>
        </ContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;